import React from 'react';
import { NavLink, Navigate, Outlet } from 'react-router-dom';
import {
  KeyIcon,
  SwatchIcon,
  PaintBrushIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';

interface DevToolsTab {
  id: string;
  label: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * Dev-only wrapper page that hosts the four legacy demo/dev routes
 * (Credentials, UI, Theme, PWA) under a single `/dev/*` namespace.
 *
 * The page itself is gated behind `import.meta.env.DEV` so it never
 * renders in a production build. The PWA tab is additionally gated on
 * `VITE_ENABLE_PWA` (matching the legacy `/pwa-demo` route gate) so it
 * is hidden from the nav when PWA tooling is disabled in dev too.
 */
const DevTools: React.FC = () => {
  // Hard-block production builds. Mirrors the gate that previously
  // wrapped the standalone /dev/credentials route in App.tsx so the
  // dev-only seed-credentials list cannot leak into a deployed bundle.
  if (!import.meta.env.DEV) {
    return <Navigate to="/dashboard" replace />;
  }

  const pwaEnabled = import.meta.env.VITE_ENABLE_PWA === 'true';

  const tabs: DevToolsTab[] = [
    { id: 'credentials', label: 'Credentials', to: '/dev/credentials', icon: KeyIcon },
    { id: 'ui', label: 'UI', to: '/dev/ui', icon: SwatchIcon },
    { id: 'theme', label: 'Theme', to: '/dev/theme', icon: PaintBrushIcon },
    ...(pwaEnabled
      ? [{ id: 'pwa', label: 'PWA', to: '/dev/pwa', icon: CubeIcon }]
      : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-testid="dev-tools-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <header className="mb-4">
          <h1 className="text-xl font-display font-bold text-gray-900 dark:text-gray-100">
            Dev Tools
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Internal-only utilities. Hidden from production builds.
          </p>
        </header>

        <nav
          className="flex border-b border-gray-200 dark:border-gray-700"
          aria-label="Dev tools sections"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.id}
                to={tab.to}
                className={({ isActive }) => `
                  inline-flex items-center px-4 py-2 text-sm font-medium
                  border-b-2 -mb-px transition-colors
                  ${isActive
                    ? 'border-primary-500 text-primary-700 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
                data-testid={`dev-tools-tab-${tab.id}`}
              >
                <Icon className="w-4 h-4 mr-2" aria-hidden="true" />
                {tab.label}
              </NavLink>
            );
          })}
        </nav>
      </div>

      <Outlet />
    </div>
  );
};

export default DevTools;
